import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import { lazy, Suspense, type ReactNode } from "react";
import { useRuntimeMode, DEVICE_TOKEN_KEY, LOCAL_ADMIN_KEY } from "./runtime";

// The venue-box image builds without VITE_CLERK_PUBLISHABLE_KEY, so main.tsx
// mounts no ClerkProvider and Clerk hooks would throw. HAS_CLERK is fixed per
// build, so choosing the hook once at module level is hooks-rules-safe.
const HAS_CLERK = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
const useAuthToken = HAS_CLERK
  ? useAuth
  : () => ({ getToken: async () => null as string | null });

// Lazy so the cloud bundle path never downloads the pairing screen.
const PairingScreen = lazy(() => import("../pages/Pairing"));

function CenteredSpinner() {
  return (
    <div className="pos min-h-screen grid place-items-center">
      <span className="inline-block w-5 h-5 rounded-full border-2 border-gray-300 border-t-mezcal animate-spin" />
    </div>
  );
}

// Cloud: Clerk gate (unchanged). Local: device-pairing gate — a paired device
// token in localStorage grants access; otherwise show the pairing screen.
export function RequireAuth({ children }: { children: ReactNode }) {
  const mode = useRuntimeMode();
  if (mode === "loading") return <CenteredSpinner />;
  if (mode === "local") {
    if (localStorage.getItem(DEVICE_TOKEN_KEY)) return <>{children}</>;
    return (
      <Suspense fallback={<CenteredSpinner />}>
        <PairingScreen />
      </Suspense>
    );
  }
  if (!HAS_CLERK) {
    // Cloud mode in a key-less build = misconfigured deployment (the box's
    // image reaching a cloud host, or a Pages build missing its .env).
    return (
      <div className="pos min-h-screen grid place-items-center p-6 text-center text-sm text-gray-500">
        Auth no configurada: este build no tiene VITE_CLERK_PUBLISHABLE_KEY y el runtime no es local.
      </div>
    );
  }
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut><RedirectToSignIn /></SignedOut>
    </>
  );
}

// Authenticated fetch — Clerk Bearer in cloud mode, X-Device-Token in local mode.
export function useApi() {
  // Hooks rules: always call the token hook; its token is unused in local mode.
  const { getToken } = useAuthToken();
  const mode = useRuntimeMode();
  return async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const headers = new Headers(init.headers || {});
    if (mode === "local") {
      const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
      if (deviceToken) headers.set("X-Device-Token", deviceToken);
      // Local-admin console writes additionally carry the local_admin session
      // token; the backend deviceAdminPolicy requires it for gated mutations.
      const la = sessionStorage.getItem(LOCAL_ADMIN_KEY);
      if (la) {
        try { const t = (JSON.parse(la) as { token?: string }).token; if (t) headers.set("X-Local-Admin-Token", t); } catch { /* corrupt — ignore */ }
      }
    } else {
      const token = await getToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
    }
    if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    const res = await fetch(path, { ...init, headers });
    if (!res.ok) {
      const text = await res.text();
      // Local mode: a revoked/unknown device token means this device must
      // re-pair. Clear it and reload — RequireAuth will show the pairing screen.
      if (mode === "local" && res.status === 401) {
        try {
          if ((JSON.parse(text) as { error?: string }).error === "device_unpaired") {
            localStorage.removeItem(DEVICE_TOKEN_KEY);
            window.location.reload();
          }
        } catch { /* non-JSON 401 body — fall through to the throw */ }
      }
      // Local-admin session expired/invalid (8h TTL): drop it and reload so the
      // console re-prompts for a PIN, instead of surfacing a raw 403.
      if (mode === "local" && res.status === 403) {
        try {
          if ((JSON.parse(text) as { error?: string }).error === "local_admin_required") {
            sessionStorage.removeItem(LOCAL_ADMIN_KEY);
            window.location.reload();
          }
        } catch { /* non-JSON 403 body — fall through to the throw */ }
      }
      throw new Error(`API ${res.status}: ${text}`);
    }
    return res.json();
  };
}
